import React, { useState, useEffect } from 'react';
import Table from 'components/Table';
import {
  Row,
  Spin,
  Form,
  Modal,
  Col,
  Input,
  TreeSelect,
} from 'antd';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import {
  getClasificadoresObjetoGasto,
  getClasificadoresObjetoGastoUnion,
  getClasificadoresObjetoGastoInternos,
  getClasificadorObjetoGastoInterno,
  postClasificadorObjetoGastoInterno,
  patchClasificadorObjetoGastoInterno,
  deleteClasificadorObjetoGastoInterno,
} from 'api/configuraciones/clasificadorObjetoGasto';
import { getEstadosGlobales } from 'api/catalogos';
import FormSubmitControls from 'components/FormSubmitControls';
import { ESTATUS_ACTIVO } from 'utils/constants';
import ModalDelete from 'components/ModalDelete';

export const clasificadorPorObjetoDelGastoPermission = {
  permissionModel: 'clasificaciondeobjetodegasto',
};

// eslint-disable-next-line react/prop-types
const ClasificadorPorObjetoDelGasto = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [dataUnion, setDataUnion] = useState([]);
  const [simpleData, setSimpleData] = useState([]);
  const [estadosGlobales, setEstadosGlobales] = useState([]);
  const [disableDelete, setDisableDelete] = useState(true);
  const [selected, setSelected] = useState();
  const [disabledVistaCompleta, setDisabledVistaCompleta] = useState(false);

  const onClickTitle = (e) => {
    const element = e.target;
    if (element) {
      let parent = element.parentElement;
      parent = parent ? parent.parentElement : null;
      if (parent) {
        const clickableSpan = parent.previousSibling;
        if (clickableSpan) {
          clickableSpan.click();
        }
      }
    }
  };

  const renderTitle = (title) => (
    // eslint-disable-next-line
    <span onClick={onClickTitle}>
      {title}
    </span>
  );

  // --- CORRECCIÓN 1: Usar CLAVE como identificador visual único ---
  const normalizeTreeCog = (_data) => {
    const newData = {};

    _data.forEach((item) => {
      const clone = { ...item };
      const title = `${clone.clave} - ${clone.concepto}`;
      clone.title = renderTitle(title);

      // CAMBIO: Usamos clave para key y value
      clone.value = clone.clave;
      clone.key = clone.clave;

      if (clone.clave_padre_id) {
        const padre = newData[clone.clave_padre_id];
        if (padre) {
          padre.seleccionable = false;
          padre.disabled = true;
          padre.children = padre.children || [];
          padre.children.push(clone);
        }
      }

      // Protegemos contra duplicados de ID (guardando el primero que aparece)
      if (!newData[clone.id]) {
        newData[clone.id] = clone;
        clone.children = clone.children || undefined;
        clone.seleccionable = true;
        clone.disabled = false;
      }
    });
    const capitulosCog = Object.values(newData).filter((item) => !item.clave_padre_id);
    return capitulosCog;
  };

  const normalizeSimpleData = (_data) => {
    const result = [];
    const processNode = (node) => {
      const copy = { ...node };
      result.push(copy);
      if (copy.children && copy.children.length) {
        copy.children.forEach((child) => {
          processNode(child);
        });
      }
      copy.children = undefined;
    };
    _data.forEach(processNode);
    return result;
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getClasificadoresObjetoGastoInternos();
    const _dataPadres = await getClasificadoresObjetoGasto();
    const _dataUnion = await getClasificadoresObjetoGastoUnion();
    const formattedCOG = normalizeTreeCog(_dataPadres);
    const formattedDataUnion = normalizeTreeCog(_dataUnion);
    const _simpleData = normalizeSimpleData(formattedDataUnion);
    setData(_data);
    setTreeData(formattedCOG);
    setDataUnion(formattedDataUnion);
    setSimpleData(_simpleData);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _dataPadres,
        _dataUnion,
        _estadosGlobales,
      ] = await Promise.all([
        getClasificadoresObjetoGastoInternos(),
        getClasificadoresObjetoGasto(),
        getClasificadoresObjetoGastoUnion(),
        getEstadosGlobales(),
      ]);
      const formattedCogs = normalizeTreeCog(_dataPadres);
      const formattedDataUnion = normalizeTreeCog(_dataUnion);
      const _simpleData = normalizeSimpleData(formattedDataUnion);
      if (mounted) {
        setData(_data);
        setTreeData(formattedCogs);
        setDataUnion(formattedDataUnion);
        setEstadosGlobales(_estadosGlobales);
        setSimpleData(_simpleData);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAlert(false);
    setSelectedRowKeys([]);
    setDisableDelete(false);
    setSelected();
    setDisabledVistaCompleta(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setDisabledVistaCompleta(false);
    const [key] = selectedRowKeys; // Esto ahora es una CLAVE

    // Buscamos por clave en el array local
    const match = responseData || data.find((item) => item.clave === key);

    if (!match) {
      setLoading(false);
      return;
    }

    const _cog = await getClasificadorObjetoGastoInterno(match.id);
    setTimeout(() => {
      if (_cog) {
        form.resetFields();

        // --- CORRECCIÓN 2: Traducir ID padre a CLAVE padre ---
        let clavePadreVisual;
        if (_cog.clave_padre) {
          // Buscamos en simpleData el objeto que tenga ese ID
          const padreObj = simpleData.find((e) => String(e.id) === String(_cog.clave_padre));
          if (padreObj) {
            clavePadreVisual = padreObj.clave;
          }
        }

        setTimeout(() => {
          form.setFieldsValue({
            ..._cog,
            clave_padre: clavePadreVisual, // Asignamos la clave al campo del TreeSelect
          });
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();

      // --- CORRECCIÓN 3: Traducir CLAVE padre a ID padre ---
      const values = {
        ...form.getFieldsValue(),
      };

      if (values.clave_padre) {
        const padreObj = simpleData.find((e) => e.clave === values.clave_padre);
        if (padreObj) {
          values.clave_padre = padreObj.id; // Enviamos el ID real
        }
      }

      const [key] = selectedRowKeys; // key es una CLAVE

      // Necesitamos el ID para editar
      let idParaEditar = null;
      if (key) {
        const itemEditar = data.find((item) => item.clave === key);
        if (itemEditar) idParaEditar = itemEditar.id;
      }

      // eslint-disable-next-line max-len
      const saved = await (key && idParaEditar ? patchClasificadorObjetoGastoInterno(idParaEditar, values) : postClasificadorObjetoGastoInterno(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.clave]); // Seleccionamos por nueva CLAVE
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onClickExpand = () => {
    setVisible(true);
    setDisabledVistaCompleta(true);
    setTimeout(() => {
      if (selected) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(selected);
          setVisible(true);
        });
      }
    });
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys; // key es CLAVE
        const itemToDelete = data.find((i) => i.clave === key);

        if (itemToDelete) {
          const deleted = await deleteClasificadorObjetoGastoInterno(itemToDelete.id);
          if (deleted) {
            setVisibleAlert(false);
            onCancel();
            await fetchData();
          }
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    // record.key es la CLAVE gracias a normalizeTreeCog
    const selectedData = data.find((e) => e.clave === record.clave);
    setSelected(record);
    if (!selectedData) {
      setDisableDelete(true);
    } else {
      setDisableDelete(false);
    }
    setSelectedRowKeys([record.key]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 350,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 900,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    clave: [
      requiredRule,
      {
        validator: async (_, value) => {
          // eslint-disable-next-line max-len
          const { clave_padre } = form.getFieldsValue(); // Obtenemos el valor actual del form (es CLAVE)

          if (!clave_padre) return; // Si no hay padre, no validamos prefijo aun

          // const numeroEnteroBooleano = /^\d+$/.test(value);

          // La clave_padre en el form ya es el string "1000", no el ID.
          // Por lo que podemos usarla directamente para validar.
          // eslint-disable-next-line max-len
          // const prefix = clave_padre.substring(0, 3); // Ojo: tu lógica original usaba substring(0,3) del padre

          // Validamos que empiece con la clave del padre (o su prefijo según tu lógica de negocio)
          // Nota: Si la regla es "iniciar con la clave padre", usa clave_padre directamente.
          // Si la regla es "iniciar con los primeros 3 digitos del padre", usa prefix.
          // Asumiré que debe iniciar con la clave del padre tal cual se muestra en el input.
          // eslint-disable-next-line max-len
          // ser un número e
          if (
            !value.toString().startsWith(clave_padre.toString().substring(0, 3))
            /* || !numeroEnteroBooleano */
          ) {
            throw new Error('La clave debe  iniciar con la clave padre y un dígito más.');
          }
          if (value.length > 10) {
            throw new Error('La clave no debe de pasar los 10 caracteres.');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={dataUnion}
          customDataArbolado={simpleData}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickExpand: () => onClickExpand(),
            onClickDelete,
          }}
          permission={permission}
          disableDelete={disableDelete}
          disableEdit={disableDelete}
          filterNested
          allowDoubleClick={false}
          allowExportPDF={false}
          rowKey="key"
          childrenProp="children"
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={disabledVistaCompleta ? 'Vista Previa' : `${selectedRowKeys.length ? 'Editar' : 'Agregar'} Clasificación de Gasto`}
              onFinish={disabledVistaCompleta ? null : onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            {!disabledVistaCompleta && (
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="clave_padre"
                  label="Clave Padre"
                  rules={rules.required}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    treeData={treeData}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    onChange={(val) => {
                      // val es la CLAVE seleccionada (ej "1000")

                      // Buscamos el objeto completo en simpleData
                      const cogPadre = simpleData.find((e) => e.clave === val);

                      if (!cogPadre) return;

                      // Tomar solo los primeros 3 dígitos del padre
                      const prefix = cogPadre.clave.substring(0, 3);

                      // Prefija la clave para que el usuario escriba el último dígito
                      form.setFieldValue('clave', prefix);
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clave"
                label="Clave"
                rules={rules.clave}
                hasFeedback
              >
                <Input
                  hasFeedback
                  disabled={disabledVistaCompleta}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="concepto"
                rules={rules.required}
                label="Concepto"
                hasFeedback
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={disabledVistaCompleta}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.required}
                label="Descripción"
                hasFeedback
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 4, maxRows: 4 }}
                  disabled={disabledVistaCompleta}
                />
              </Form.Item>
            </Col>
            {!disabledVistaCompleta && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estadosGlobales}
                    disabled
                  />
                </Form.Item>
              </Col>
            )}
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={selected ? `la Clasificación por Objeto del Gasto ${selected.concepto}` : ''}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ClasificadorPorObjetoDelGasto;
